import type { TFunction } from "i18next";
import type { FusionMenuConfig } from "~/components/dock/FusionMenu";

import { ApplicationEnum } from "~/@types/enums";
import { isRestockDockPageEnabled, type RestockDockOptions } from "~restock/dock/restockDockPageRegistry";

const createRestockBaseConfig = (t: TFunction<"common">): FusionMenuConfig => ({
	defaultQuickActionKeys: ["load-list", "orders"],
	logo: "/logos/restock.svg",
	module: ApplicationEnum.RESTOCK,
	quickActions: [
		{
			icon: "format_list_bulleted",
			label: t("dock.quickActionLabels.restock.loadList"),
			pageId: "load-list"
		},
		{ icon: "shopping_cart", label: t("dock.quickActionLabels.restock.orders"), pageId: "orders" },
		{ icon: "insights", label: t("dock.quickActionLabels.restock.dashboard"), pageId: "dashboard" }
	],
	sections: [
		{
			groups: [{ items: [{ icon: "insights", pageId: "dashboard" }] }],
			icon: "insights",
			id: "dashboard",
			label: t("dock.pages.restock.dashboard")
		},
		{
			groups: [{ items: [{ icon: "format_list_bulleted", pageId: "load-list" }] }],
			icon: "format_list_bulleted",
			id: "load-list",
			label: t("dock.pages.restock.loadList")
		},
		{
			groups: [
				{
					items: [
						{ icon: "shopping_cart", label: t("dock.pages.restock.orders"), pageId: "orders" },
						{ icon: "widgets", label: t("dock.pages.restock.kitBuilder"), pageId: "kit-builder" }
					]
				}
			],
			icon: "shopping_cart",
			id: "orders",
			label: t("dock.pages.restock.orders")
		},
		{
			groups: [
				{
					items: [
						{ icon: "tune", label: t("dock.pages.restock.dataManagementDefaults"), pageId: "data-management-defaults" },
						{ icon: "list_alt", label: t("dock.pages.restock.dataManagementItems"), pageId: "data-management-items" },
						{
							icon: "location_on",
							label: t("dock.pages.restock.dataManagementLocations"),
							pageId: "data-management-locations"
						},
						{
							icon: "storefront",
							label: t("dock.pages.restock.dataManagementStoreClasses"),
							pageId: "data-management-store-classes"
						},
						{
							icon: "local_shipping",
							label: t("dock.pages.restock.dataManagementDeliverySchedules"),
							pageId: "data-management-delivery-schedules"
						},
						{
							icon: "package_2",
							label: t("dock.pages.restock.dataManagementContainers"),
							pageId: "data-management-containers"
						},
						{ icon: "rule", label: t("dock.pages.restock.dataManagementRules"), pageId: "data-management-rules" }
					]
				}
			],
			icon: "database",
			id: "data-management",
			label: t("dock.pages.restock.sectionDataManagement")
		}
	]
});

const FLAG_GATED_SECTION_PAGE_IDS: Record<string, string> = {
	dashboard: "dashboard",
	"data-management": "data-management-defaults",
	orders: "orders"
};

const createRestockFusionConfig = (t: TFunction<"common">, options: RestockDockOptions): FusionMenuConfig => {
	const restockFusionConfig = createRestockBaseConfig(t);
	return {
		...restockFusionConfig,
		quickActionKeySource: restockFusionConfig.quickActions,
		quickActions: (restockFusionConfig.quickActions ?? []).filter(
			(action) => !action.pageId || isRestockDockPageEnabled(action.pageId, options)
		),
		sections: restockFusionConfig.sections.filter((section) => {
			const gatingPageId = FLAG_GATED_SECTION_PAGE_IDS[section.id];
			return !gatingPageId || isRestockDockPageEnabled(gatingPageId, options);
		})
	};
};

export default createRestockFusionConfig;
